import axios, { AxiosResponse } from "axios";
import {
  ApiResponseGetItemDetails,
  ApiResponseGetPastOrderCustomerData,
  ApiResponseGetPaymentLink,
  ApiResponseGetRecommendedItems,
  ApiResponseGetSchoolDetails,
  ApiResponseGetStoreItems,
  DataLinks,
  DefaultAndRecommendedItem,
  FinancialData,
  ItemDetailsData,
  PaymentLink,
  RecommendedItem,
  SchoolDetails,
  getAvailableDatesForExamResponse,
  getOrderStatusResponse,
} from "../DefinitionsInterfaces";
import * as Sentry from "@sentry/react";
import api from "../axios/axiosMW";
import { ServerResponseGeneral } from "../api/auth";

// Coupon interfaces
export interface CouponCodeRequest {
  coupon_code: string;
  currentAmount: number;
}

export interface CouponCodeResponseData {
  coupon_code: string;
  discount_amount: number;
  priceAfterDiscount: number;
  priceBeforeDiscount: number;
}

export interface CouponCodeResponse {
  status: string;
  data: CouponCodeResponseData;
  message: string;
}

const fetchItemsList = async (): Promise<void> => {
  try {
    const response: AxiosResponse<ApiResponseGetStoreItems> = await api.post(
      "/get-store-items",
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response = ", response);
    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      console.log("ItemsObtains");
    } else {
      throw "Invalid response for student search";
    }
  } catch (error) {
    console.log("API failed with error : ", error);
  }
};

export const fetchItemDetails = async (
  itemId: number
): Promise<ItemDetailsData[]> => {
  let foundItems: ItemDetailsData[] = [];
  try {
    const response: AxiosResponse<ApiResponseGetItemDetails> = await api.post(
      "/get-details-for-item",
      {
        itemID: itemId,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response = ", response);
    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      foundItems.push(response.data.data);
      // setCheckoutItems(response.data.data.items);
    } else {
      throw "Invalid response for student search";
    }
  } catch (error) {
    throw new Error(`API failed with error : ${error}`);
  }
  return foundItems;
};

export const getSchoolByCode = async (
  schoolCode: string
): Promise<SchoolDetails> => {
  let schoolDetails: SchoolDetails = {
    schoolID: 0,
    schoolName: "",
    linkActive: false,
    city: "",
    state: "",
    individual_registration: false,
    examDate: "",
    isInternational: false,
    version_no: "V1",
    pg: "razorpay",
    pincode: "560076",
  };
  try {
    const response: AxiosResponse<ApiResponseGetSchoolDetails> = await api.post(
      "/get-school-by-code",
      {
        schoolCode: schoolCode,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response for getting school by code = ", response);
    if (
      response &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      schoolDetails = response.data.data;
    } else {
      throw "Invalid Response";
    }
  } catch (err) {
    throw new Error(`Error getting school by code : ${err},`);
  }
  return schoolDetails;
};

export const fetchRecommendedItems = async (
  schoolCode: string,
  mobileNumber: string,
  pfh: boolean,
  referredBy: string | null,
  rollNumber?: string | null,
  nfo2024?: boolean
): Promise<DefaultAndRecommendedItem> => {
  let foundItems: DefaultAndRecommendedItem;
  try {
    const response: AxiosResponse<ApiResponseGetRecommendedItems> =
      await api.post(
        "/get-recommended-items-self-checkout",
        {
          schoolCode: schoolCode,
          ...(pfh ? { phone: mobileNumber } : {}),
          ...(referredBy ? { referredBy: referredBy } : {}),
          ...(rollNumber ? { rollNumber: rollNumber } : {}),
          ...(nfo2024 ? { nfo2024: nfo2024 } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    console.log("🔍 Recommended items API Response:", {
      url: "/get-recommended-items-self-checkout",
      requestParams: {
        schoolCode,
        ...(pfh ? { phone: mobileNumber } : {}),
        ...(referredBy ? { referredBy: referredBy } : {}),
        ...(rollNumber ? { rollNumber: rollNumber } : {}),
        ...(nfo2024 ? { nfo2024: nfo2024 } : {}),
      },
      response: response.data,
    });
    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      foundItems = response.data.data;
    } else {
      throw "Invalid response for student search";
    }
  } catch (error) {
    foundItems = {
      default: {
        name: "",
        image: "",
        subtitle: "",
        description: "",
        base_price: 0,
        selling_price: 0,
        autoApplyRollAsCoupon: false,
        pfhPrice: 0,
        nfo2024Price: 0,
      },
      recommendedItems: [],
    };
    throw new Error(`API failed with error : ${error}`);
  }
  return foundItems;
};

// Autopay (₹2 try-now) A/B arm — mirrors fetchRecommendedItems but hits the
// flat-pricing endpoint. Backend accepts the same inputs but always returns ₹2.
export const getRecommendedItemsAutopay = async (
  schoolCode: string,
  phone?: string | null,
  referredBy?: string | null,
  rollNumber?: string | null
): Promise<DefaultAndRecommendedItem> => {
  const response: AxiosResponse<ApiResponseGetRecommendedItems> =
    await api.post(
      "/get-recommended-items-self-checkout-autopay",
      {
        schoolCode: schoolCode,
        ...(phone ? { phone: phone } : {}),
        ...(referredBy ? { referredBy: referredBy } : {}),
        ...(rollNumber ? { rollNumber: rollNumber } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  if (
    response &&
    response.status === 200 &&
    response.data &&
    response.data.status_code === 200 &&
    response.data.data
  ) {
    return response.data.data;
  }
  throw new Error("Invalid response from autopay recommended items endpoint");
};

export const getPaymentLinkForOrder = async (data: {
  orderNote: string;
}): Promise<AxiosResponse<ApiResponseGetPaymentLink>> => {
  //@ts-ignore
  let response: AxiosResponse<ApiResponseGetPaymentLink> = {
    status: 400,
    statusText: "Failed",
    data: {
      status: "Failed",
      status_code: 400,
      data: {
        orderID: "",
        links: [
          {
            href: "",
            rel: "",
            method: "",
          },
        ],
      },
      message: "",
    },
  };
  try {
    response = await axios.post(
      "https://streak-payments.el.r.appspot.com/nfo/get-payment-link-for-order",
      {
        ...data,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response = ", response);

    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      console.log("Successful Response", response);
    } else {
      throw "Invalid response Get Payment Link";
    }
  } catch (error) {
    throw new Error(`API failed with error :${error} `);
  }
  return response;
};

export const getPastOrderCustomerData = async (): Promise<
  AxiosResponse<ApiResponseGetPastOrderCustomerData>
> => {
  //@ts-ignore
  let response: AxiosResponse<ApiResponseGetPastOrderCustomerData> = {
    status: 400,
    statusText: "Failed",
    data: {
      status: "Failed",
      status_code: 400,
      data: [],
      message: "",
    },
  };
  try {
    response = await axios.post(
      "https://streak-onboarding.el.r.appspot.com/nfo/get-past-students",
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response = ", response);

    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      console.log("Successful Response", response);
    } else {
      throw "Invalid response Get Payment Link";
    }
  } catch (error) {
    throw new Error(`API failed with error :${error} `);
  }
  return response;
};
export const getAvailableDatesForExam = async (): Promise<
  AxiosResponse<getAvailableDatesForExamResponse>
> => {
  //@ts-ignore
  let response: AxiosResponse<getAvailableDatesForExamResponse> = {
    status: 400,
    statusText: "Failed",
    data: {
      status: "Failed",
      status_code: 400,
      data: [
        {
          schoolID: 0,
          examDate: "",
        },
      ],
      message: "",
    },
  };
  try {
    response = await axios.post(
      "https://streak-onboarding.el.r.appspot.com/nfo/get-dates-for-ir-school",
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("Response = ", response);

    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.status_code === 200 &&
      response.data.data
    ) {
      console.log("Successful Response", response);
    } else {
      throw "Invalid response Get Payment Link";
    }
  } catch (error) {
    throw new Error(`API failed with error :${error} `);
  }
  return response;
};

export function getOrderStatus(
  orderID: string
): Promise<getOrderStatusResponse> {
  return axios.post(
    "https://streak-payments.el.r.appspot.com/nfo/verify-payment-for-order",
    {
      orderID: orderID,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export interface doNFOTransactionResponse {
  data: {
    orderID: string;
    links: [
      {
        href: string;
        rel: string;
        method: string;
      },
      {
        href: string;
        rel: string;
        method: string;
        parameters: {
          mercid: string;
          bdorderid: string;
          rdata: string;
        };
        valid_date: string;
        headers: {
          authorization: string;
        };
      }
    ];
  };
}

export interface doNFOTransactionResponseRazorpay {
  message: {
    amount: number;
    amount_due: number;
    amount_paid: number;
    attempts: number;
    created_at: number;
    currency: string;
    entity: string;
    id: string;
    notes: {
      choosenItemIDs: number[];
      email: string;
      grade: number;
      mobileNumber: string;
      name: string;
      referredBy: string | null;
      schoolID: number;
      schoolName: string;
    };
    offer_id: null;
    receipt: string;
    status: string;
  };
}

export function doNFOTransaction(
  name: string,
  schoolID: string,
  grade: number,
  mobileNumber: string,
  email: string,
  choosenItemIDs: number[],
  school: string,
  individualReg: boolean,
  referredBy?: string | null,
  nfo2024?: boolean | null
): Promise<doNFOTransactionResponse> {
  return axios.post(
    "https://streak-payments.el.r.appspot.com/nfo/nfo-payment-link",
    individualReg
      ? referredBy
        ? {
            name: name,
            schoolID: schoolID,
            grade: grade,
            mobileNumber: mobileNumber,
            email: email,
            choosenItemIDs: choosenItemIDs,
            schoolName: school,
            referredBy: referredBy ? referredBy : null,
            nfo2024: nfo2024 ? nfo2024 : false,
          }
        : {
            name: name,
            schoolID: schoolID,
            grade: grade,
            mobileNumber: mobileNumber,
            email: email,
            choosenItemIDs: choosenItemIDs,
            schoolName: school,
            nfo2024: nfo2024 ? nfo2024 : false,
          }
      : {
          name: name,
          schoolID: schoolID,
          grade: grade,
          mobileNumber: mobileNumber,
          email: email,
          choosenItemIDs: choosenItemIDs,
          nfo2024: nfo2024 ? nfo2024 : false,
        },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export function doNFOTransactionRazorpay(
  name: string,
  schoolID: string,
  grade: number,
  mobileNumber: string,
  email: string,
  choosenItemIDs: number[],
  school: string,
  individualReg: boolean,
  referredBy?: string | null,
  pincode?: string | null,
  city?: string | null,
  state?: string | null,
  pfh?: boolean | null,
  nfo2024?: boolean | null,
  couponCode?: string
): Promise<doNFOTransactionResponseRazorpay> {
  const channel =
    typeof window !== "undefined"
      ? sessionStorage.getItem("exp_name") || null
      : null;
  const sessionId =
    typeof document !== "undefined"
      ? document.cookie
          .split(";")
          .find((cookie) => cookie.trim().startsWith("session_id="))
          ?.split("=")[1] || null
      : null;

  console.log("channel", channel);

  return axios.post(
    "https://streak-onboarding.el.r.appspot.com/nfo/create-selfcheckout-order",
    individualReg
      ? referredBy
        ? {
            name: name,
            schoolID: null,
            grade: grade,
            mobileNumber: mobileNumber,
            email: email,
            choosenItemIDs: choosenItemIDs,
            schoolName: school,
            referredBy: referredBy ? referredBy : null,
            pincode: pincode ? pincode : null,
            city: city ? city : null,
            state: state ? state : null,
            pfh: pfh ? pfh : false,
            nfo2024: nfo2024 ? nfo2024 : false,
            ...(couponCode && { couponCode: couponCode }),
            ...(channel && { channel }),
            ...(sessionId && { sessionId }),
            irs: true,
          }
        : {
            name: name,
            schoolID: null,
            grade: grade,
            mobileNumber: mobileNumber,
            email: email,
            choosenItemIDs: choosenItemIDs,
            schoolName: school,
            pincode: pincode ? pincode : null,
            city: city ? city : null,
            state: state ? state : null,
            pfh: pfh ? pfh : false,
            nfo2024: nfo2024 ? nfo2024 : false,
            ...(couponCode && { couponCode: couponCode }),
            ...(channel && { channel }),
            ...(sessionId && { sessionId }),
            irs: true,
          }
      : {
          name: name,
          schoolID: schoolID,
          grade: grade,
          mobileNumber: mobileNumber,
          email: email,
          choosenItemIDs: choosenItemIDs,
          pincode: pincode ? pincode : null,
          city: city ? city : null,
          state: state ? state : null,
          pfh: pfh ? pfh : false,
          nfo2024: nfo2024 ? nfo2024 : false,
          ...(couponCode && { couponCode: couponCode }),
          ...(channel && { channel }),
          ...(sessionId && { sessionId }),
        },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export function verifyNFOTransaction(
  orderID: string
): Promise<doNFOTransactionResponse> {
  return axios.post(
    "https://streak-payments.el.r.appspot.com/nfo/verify-nfo-order",
    {
      orderID: orderID,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
}

export interface verifyNFOTransactionResponseRazorpay {
  status: "Success" | "Error";
  status_code: 200;
  data: {
    id: string;
    entity: string;
    amount: number;
    amount_paid: number;
    amount_due: number;
    currency: string;
    receipt: string;
    offer_id: null;
    status: "paid" | "attempted" | "created";
    attempts: number;
    notes: {
      name: string;
      email: string;
      grade: number;
      schoolID: number;
      referredBy: string | null;
      schoolName: string;
      mobileNumber: string;
      choosenItemIDs: number[];
    };
    created_at: number;
  };
  message: "";
}

export function verifyNFOTransactionRazorpay(
  orderID: string
): Promise<verifyNFOTransactionResponseRazorpay> {
  return axios
    .post(
      "https://streak-onboarding.el.r.appspot.com/nfo/razorpay-verify-payment",
      {
        orderID: orderID,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then(
      (response: AxiosResponse<verifyNFOTransactionResponseRazorpay>) =>
        response.data
    ); // Return only the data;
}
export function razorpaySaveDetails(
  razorpay_payment_id: string,
  razorpay_order_id: string,
  razorpay_signature: string
): Promise<verifyNFOTransactionResponseRazorpay> {
  return axios
    .post(
      "https://streak-onboarding.el.r.appspot.com/nfo/razorpay-save-details",
      {
        razorpay_payment_id: razorpay_payment_id,
        razorpay_order_id: razorpay_order_id,
        razorpay_signature: razorpay_signature,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then(
      (response: AxiosResponse<verifyNFOTransactionResponseRazorpay>) =>
        response.data
    ); // Return only the data;
}

interface nfoFecthUserNameResponse {
  status: "Success" | "Error";
  status_code: 200;
  data: {
    studentUsername: string;
  };
  message: "";
}

export function nfoFetchUserName(
  orderID: string
): Promise<nfoFecthUserNameResponse> {
  return axios
    .post(
      "https://streak-onboarding.el.r.appspot.com/nfo/fetch-username",
      {
        orderID: orderID,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then((response: AxiosResponse<nfoFecthUserNameResponse>) => response.data);
}

export interface GetStudentPostPaymentResponse {
  status: "Success" | "Error";
  status_code: number;
  data: {
    studentUsername: string;
    token?: string | null;
    refresh_token?: string | null;
    device_id?: string;
    active_devices?: number | { phone: string; active_devices: number; current_device_ids: string[] };
  };
  message: string;
}

export function getStudentPostPayment(
  orderID: string,
  options?: { newhub: boolean; gettoken: boolean; device_id: string }
): Promise<GetStudentPostPaymentResponse> {
  const body: Record<string, unknown> = { orderID };
  if (options) {
    body.newhub = options.newhub;
    body.gettoken = options.gettoken;
    body.device_id = options.device_id;
  }
  return axios
    .post(
      "https://streak-onboarding.el.r.appspot.com/nfo/newhubapis/onboarding/get-student-post-payment",
      body,
      { headers: { "Content-Type": "application/json" } }
    )
    .then((response: AxiosResponse<GetStudentPostPaymentResponse>) => response.data);
}

interface OtpResponse extends ServerResponseGeneral {
  data: string;
}

interface VerifyOtpResponse extends ServerResponseGeneral {
  data: string;
}

interface VerifyOtpPFHResponse extends ServerResponseGeneral {
  data: {
    pfh: boolean;
    nfo2024: boolean;
  };
}

// ─── Newhub verify-otp (nfo-stadia parity) ───────────────────────────────────

export type NewhubStudentProfile = {
  student_username: string;
  cuid: string;
  name: string;
  grade: number;
  school_name: string;
  school_id: number | null;
  olympiad_year: number | null;
  allow_login: boolean;
  registration_completed: boolean;
  password_set: boolean;
  examDate: string;
  school: string;
  examLink: string | null;
  is_temp_profile: boolean;
};

export type NewhubVerifyOtpResponseData = {
  code: string;
  message: string;
  is_nfo_customer: boolean;
  reserved_cuid?: string;
  is_pfh_customer: boolean;
  password_set: boolean;
  profiles: NewhubStudentProfile[];
  pfh_order_type: string | null;
  cod_delivered: boolean | null;
  tracking_url: string | null;
  book_count: number;
  token: string;
  refresh_token: string;
  active_devices: number;
  device_id: string;
  reused_existing_token: boolean;
  profileLinked?: boolean;
  gradeSchoolSelected?: boolean;
  profile_count?: number;
  name?: string;
  grade?: number | string | null;
  school_name?: string | null;
  school_id?: number | null;
  email?: string;
  state?: string;
  city?: string;
  pincode?: string;
  address?: string;
  registration_completed?: boolean;
};

export interface NewhubVerifyOtpResponse {
  status: "Success" | "Error";
  status_code: number;
  message: string;
  data: NewhubVerifyOtpResponseData;
}

const NEWHUB_BASE = "https://streak-onboarding.el.r.appspot.com/nfo/newhubapis";

export type AddPfhStudentPayload = {
  phone: string;
  student_name: string;
  school_name: string;
  grade: number;
  email: string;
};

export type AddPfhStudentResponse = {
  status: "Success" | "Error";
  status_code: number;
  message: string;
  data: {
    created: boolean;
    temp_profile_id: number;
    student_username: string;
    nfo_active: boolean;
  };
};

export const addPfhStudent = async (
  payload: AddPfhStudentPayload,
  token: string
): Promise<AddPfhStudentResponse> => {
  const response = await axios.post<AddPfhStudentResponse>(
    `${NEWHUB_BASE}/onboarding/add-pfh-student`,
    payload,
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const searchSchools = async (
  q: string,
  signal?: AbortSignal
): Promise<{ id: number; name: string; display_label: string; city: string; state: string }[]> => {
  const url = `${NEWHUB_BASE}/onboarding/school-search?${new URLSearchParams({ q, limit: "50" })}`;
  const res = await fetch(url, { signal });
  const json = await res.json();
  return json?.data?.schools ?? [];
};

export const verifyOtpNewhub = async (
  phone: string,
  otp: string,
  device_id: string
): Promise<NewhubVerifyOtpResponse> => {
  const response = await axios.post<NewhubVerifyOtpResponse>(
    `${NEWHUB_BASE}/auth/verify-otp`,
    { phone, otp, device_id, forRegistration: true },
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data;
};

export const sendOtpNewhub = async (
  phone: string,
  device_id: string
): Promise<OtpResponse> => {
  const response = await axios.post<OtpResponse>(
    `${NEWHUB_BASE}/auth/phone-submit`,
    { phone, device_id, sendOtp: true },
    { headers: { "Content-Type": "application/json" } }
  );
  return response.data;
};

export const sendOtpAPI = async (phone: string): Promise<OtpResponse> => {
  try {
    const response = await axios.post<OtpResponse>(
      "https://streak-onboarding.el.r.appspot.com/nfo/send-otp-self-checkout",
      { phone },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};

export const sendOtpAPIPFH = async (phone: string): Promise<OtpResponse> => {
  try {
    const response = await axios.post<OtpResponse>(
      "https://streak-onboarding.el.r.appspot.com/nfo/send-otp-self-checkout-pfh",
      { phone },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};

export const verifyOtpAPI = async (
  phone: string,
  otp: string
): Promise<VerifyOtpResponse> => {
  try {
    const response = await axios.post<VerifyOtpResponse>(
      "https://streak-onboarding.el.r.appspot.com/nfo/verify-otp-selfcheckout",
      { phone, otp },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying OTP:", error);
    throw error;
  }
};

export const verifyOtpPFHAPI = async (
  phone: string,
  otp: string
): Promise<VerifyOtpPFHResponse> => {
  try {
    const response = await axios.post<VerifyOtpPFHResponse>(
      "https://streak-onboarding.el.r.appspot.com/nfo/verify-otp-selfcheckout-pfh",
      { phone, otp },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying PFH OTP:", error);
    throw error;
  }
};

export interface AddressFromPincodeResponse {
  status: "Success" | "Error";
  status_code: number;
  data: {
    state: string;
    city: string;
  };
  message: string;
}

export async function getAddressFromPincodeAPI(
  pincode: string
): Promise<AddressFromPincodeResponse> {
  try {
    const response = await axios.get(
      `https://streak-onboarding.el.r.appspot.com/lq/get-address-from-pincode?pincode=${pincode}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(`Failed to get address from pincode: ${error}`);
  }
}

interface FormSparkSubmitResponse extends ServerResponseGeneral {
  data: string;
}

interface UserDetailsData {
  name: string;
  school: string;
  mobileNumber: string;
  email: string;
  grade: number;
  pincode: string;
  city: string;
  state: string;
}
export async function formSparkSubmit(
  userDetails: UserDetailsData
): Promise<AxiosResponse<FormSparkSubmitResponse>> {
  try {
    const response = await axios.post<FormSparkSubmitResponse>(
      "https://submit-form.com/SOszeJIbb",
      userDetails,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    throw new Error(`Failed to submit form: ${error}`);
  }
}

// ─── Autopay / Mandate APIs ───────────────────────────────────────────────────

const MANDATE_BASE = "https://streak-onboarding.el.r.appspot.com/nfo/newhubapis/onboarding";

export interface MandateSessionResp {
  created: boolean;
  // Single Razorpay order — recurring:1 both charges `amount` upfront AND
  // registers the autopay mandate in one checkout. No second order/screen.
  order: { id: string; amount: number; currency: string; status: string };
  amount: number; // paise charged upfront (₹2, or full fee if churned)
  customer_id: string;
  currency: "INR";
  student_token: string;
  is_churned_resubscribe: boolean;
  full_amount_paise: number;
  max_amount_paise: number;
  mandate_row_id: number;
  olympiad_year: number;
  error?: string;
}

export async function createMandateSession(body: {
  name: string;
  phone: string;
  grade: number;
  pincode?: string;
  email?: string;
  mandate_method?: "upi" | "emandate" | "card";
  is_pfh_user?: boolean;
  nfo_username?: string;
  referred_by?: string;
}): Promise<MandateSessionResp> {
  const res = await axios.post(`${MANDATE_BASE}/mandate-session`, body, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data.data;
}

export async function cancelMandate(body: {
  student_username?: string;
  phone?: string;
}): Promise<{ ok: boolean; [key: string]: unknown }> {
  const token = sessionStorage.getItem("newhubToken") ?? "";
  const res = await axios.post(`${MANDATE_BASE}/mandate-cancel`, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data.data;
}

export async function collectMandateNow(body: {
  student_username: string;
  olympiad_year?: number;
}): Promise<{ ok: boolean; [key: string]: unknown }> {
  const token = sessionStorage.getItem("newhubToken") ?? "";
  const res = await axios.post(`${MANDATE_BASE}/mandate-collect-now`, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data.data;
}

// ─── Checkout Initiated Relay ─────────────────────────────────────────────────
// Sends the checkout_initiated event to the backend, which sets WebEngage
// attributes and fires the event on the CUID. This is a fail-safe relay —
// never call the WebEngage event API directly for this funnel.

export interface TrackCheckoutInitiatedPayload {
  cuid: string;
  name?: string;
  email?: string;
  phone?: string;
  grade?: number;
  is_pfh_user?: boolean;
  event_data?: Record<string, unknown>;
}

export async function trackCheckoutInitiated(
  payload: TrackCheckoutInitiatedPayload
): Promise<void> {
  try {
    await axios.post(
      `${NEWHUB_BASE}/onboarding/track-checkout-initiated`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );
  } catch (error) {
    // Fail-safe: analytics relay must never block the user flow
    console.error("track-checkout-initiated relay failed (non-blocking):", error);
  }
}

// Coupon API function
export async function getCouponDiscount(
  couponCode: string,
  currentAmount: number,
  autoApplyRollAsCoupon: boolean = false
): Promise<CouponCodeResponse> {
  try {
    const response = await axios.post<CouponCodeResponse>(
      "https://streak-onboarding.el.r.appspot.com/nfo/get-discount-for-coupon-code",
      {
        coupon_code: couponCode,
        currentAmount: currentAmount,
        programType: "NFO",
        autoApplyRollAsCoupon: autoApplyRollAsCoupon,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}
